import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Tv, Save, Loader2, ExternalLink, Sparkles, Wine, Check, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const THEMES = [
  {
    id: 'quirky',
    name: 'Quirky',
    icon: Sparkles,
    img: '/tv/preview-quirky.jpg',
    blurb: 'Playful and bold. Stickers, Hinglish, vinyl and TV-box visuals. Best for bars, cafes, pubs and casual dining.',
  },
  {
    id: 'premium',
    name: 'Premium',
    icon: Wine,
    img: '/tv/preview-premium.jpg',
    blurb: 'Calm and elegant. Serif type, gold hairlines, quiet motion. Best for fine dining, hotels and lounges.',
  },
];

const PALETTES = [
  { id: 'noir', name: 'Noir', swatch: ['#0B0B0C', '#C9A96A'] },
  { id: 'ivory', name: 'Ivory', swatch: ['#F6F1E7', '#A6803F'] },
  { id: 'emerald', name: 'Emerald', swatch: ['#0E2A24', '#C8A862'] },
];

/** Settings card: pick the per-venue TV Display Theme (Quirky / Premium). */
export function TVThemeCard() {
  const [venueCode, setVenueCode] = useState('');
  const [theme, setTheme] = useState('quirky');
  const [palette, setPalette] = useState('noir');
  const [accent, setAccent] = useState('');
  const [city, setCity] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [geoLabel, setGeoLabel] = useState('');
  const [geoBusy, setGeoBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [code, prof] = await Promise.all([
        axios.get(`${API}/venue/my-code`),
        axios.get(`${API}/venue/profile`),
      ]);
      setVenueCode(code.data?.venue_code || '');
      const p = prof.data || {};
      setTheme(p.tv_theme === 'premium' ? 'premium' : 'quirky');
      setPalette(['noir', 'ivory', 'emerald'].includes(p.tv_palette) ? p.tv_palette : 'noir');
      setAccent(p.tv_accent || '');
      setCity(p.tv_city || '');
      setLat(p.tv_lat != null ? String(p.tv_lat) : '');
      setLon(p.tv_lon != null ? String(p.tv_lon) : '');
    } catch (e) {
      // silent — show defaults
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolveCity = async (opts = {}) => {
    const name = city.trim();
    if (!name) { if (!opts.silent) toast.error('Type a city first'); return; }
    setGeoBusy(true);
    try {
      const r = await axios.get(`${API}/venue/geocode`, { params: { q: name } });
      setLat(String(r.data.lat));
      setLon(String(r.data.lon));
      setGeoLabel(r.data.label || '');
      if (!opts.silent) toast.success(`Located: ${r.data.label || name}`);
    } catch (e) {
      setGeoLabel('');
      if (!opts.silent) toast.error(e?.response?.data?.detail || "Couldn't find that city");
    } finally {
      setGeoBusy(false);
    }
  };

  const accentForUrl = accent ? accent.replace('#', '') : '';
  const previewSrc = useMemo(() => {
    if (!venueCode) return '';
    let u = `/tv/${theme}.html?venue=${encodeURIComponent(venueCode)}&demo=0&preview=1`;
    if (BACKEND) u += `&api=${encodeURIComponent(BACKEND)}`;
    if (theme === 'premium') {
      u += `&palette=${palette}`;
      if (accentForUrl) u += `&accent=%23${accentForUrl}`;
    }
    return u;
  }, [venueCode, theme, palette, accentForUrl]);

  const openOnTV = () => {
    if (!venueCode) return;
    let u = `/tv/${theme}.html?venue=${encodeURIComponent(venueCode)}&demo=0&preview=1`;
    if (theme === 'premium') {
      u += `&palette=${palette}`;
      if (accentForUrl) u += `&accent=%23${accentForUrl}`;
    }
    window.open(u, '_blank', 'noopener,noreferrer');
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        tv_theme: theme,
        tv_palette: palette,
        tv_accent: accent || '',
        tv_city: city,
      };
      if (lat.trim() !== '') payload.tv_lat = parseFloat(lat);
      if (lon.trim() !== '') payload.tv_lon = parseFloat(lon);
      await axios.put(`${API}/venue/profile`, payload);
      toast.success('TV theme saved — screens update within ~30s');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save theme');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 flex items-center justify-center" data-testid="tv-theme-card">
        <Loader2 className="w-6 h-6 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl" data-testid="tv-theme-card">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-11 h-11 rounded-2xl bg-[#FF007F]/15 ring-1 ring-[#FF007F]/40 flex items-center justify-center flex-shrink-0">
          <Tv className="w-5 h-5 text-[#FF007F]" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-extrabold tracking-tight">TV Display Theme</h3>
          <p className="text-sm text-gray-400 mt-0.5">
            Choose how your venue TV screen looks. Changes reach a running TV within ~30 seconds — no need to touch the screen.
          </p>
        </div>
      </div>

      {/* Theme selector cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        {THEMES.map((t) => {
          const Icon = t.icon;
          const active = theme === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTheme(t.id)}
              className={`relative text-left rounded-2xl overflow-hidden ring-1 transition-all ${active ? 'ring-2 ring-[#FF007F]' : 'ring-white/10 hover:ring-white/30'}`}
              data-testid={`tv-theme-option-${t.id}`}
              aria-pressed={active}
            >
              <div className="aspect-video bg-black overflow-hidden">
                <img src={t.img} alt={`${t.name} theme preview`} className="w-full h-full object-cover" />
              </div>
              {active && (
                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#FF007F] flex items-center justify-center" data-testid={`tv-theme-active-${t.id}`}>
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
              )}
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className="w-4 h-4 text-[#FF007F]" />
                  <p className="font-bold text-white">{t.name}</p>
                </div>
                <p className="text-[11px] text-gray-400 leading-relaxed">{t.blurb}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Premium palette + accent */}
      {theme === 'premium' && (
        <div className="rounded-2xl border border-white/10 bg-black/20 p-4 mb-5" data-testid="tv-premium-options">
          <p className="text-xs font-bold text-white uppercase tracking-wider mb-3">Premium palette</p>
          <div className="flex flex-wrap gap-2 mb-4">
            {PALETTES.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setPalette(p.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl ring-1 transition-all ${palette === p.id ? 'ring-2 ring-[#FF007F] bg-white/5' : 'ring-white/10 hover:ring-white/30'}`}
                data-testid={`tv-palette-${p.id}`}
                aria-pressed={palette === p.id}
              >
                <span className="flex">
                  <span className="w-4 h-4 rounded-l-full" style={{ background: p.swatch[0] }} />
                  <span className="w-4 h-4 rounded-r-full" style={{ background: p.swatch[1] }} />
                </span>
                <span className="text-sm text-white">{p.name}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Accent colour</label>
            <input
              type="color"
              value={accent || '#C9A96A'}
              onChange={(e) => setAccent(e.target.value)}
              className="w-9 h-9 rounded-lg bg-transparent border border-white/10 cursor-pointer"
              data-testid="tv-accent-picker"
            />
            {accent ? (
              <button type="button" onClick={() => setAccent('')} className="text-[11px] text-gray-500 hover:text-white underline" data-testid="tv-accent-reset">
                Use palette default
              </button>
            ) : (
              <span className="text-[11px] text-gray-500">Defaults to the palette gold</span>
            )}
          </div>
        </div>
      )}

      {/* Weather location */}
      <div className="rounded-2xl border border-white/10 bg-black/20 p-4 mb-5" data-testid="tv-weather-location">
        <p className="text-xs font-bold text-white uppercase tracking-wider mb-1">Weather location</p>
        <p className="text-[11px] text-gray-500 mb-3">Type your city and we'll find the coordinates automatically for the on-screen weather.</p>
        <div className="flex gap-2">
          <Input
            value={city}
            onChange={(e) => { setCity(e.target.value.slice(0, 60)); setGeoLabel(''); }}
            onBlur={() => { if (city.trim() && !(lat && lon)) resolveCity({ silent: true }); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); resolveCity(); } }}
            placeholder="City (e.g. Mumbai)"
            className="bg-black/40 border-white/10 text-white h-10 rounded-xl flex-1"
            data-testid="tv-city-input"
          />
          <Button
            type="button"
            onClick={() => resolveCity()}
            disabled={geoBusy || !city.trim()}
            variant="secondary"
            className="rounded-xl h-10 px-4 text-[11px] font-bold tracking-wider gap-1.5"
            data-testid="tv-geocode-btn"
          >
            {geoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
            FIND
          </Button>
        </div>
        {(lat && lon) ? (
          <p className="text-[11px] text-[#39FF14] mt-2 flex items-center gap-1.5" data-testid="tv-geocode-result">
            <MapPin className="w-3 h-3" />
            {geoLabel ? `${geoLabel} · ` : ''}{parseFloat(lat).toFixed(3)}, {parseFloat(lon).toFixed(3)}
            <button type="button" onClick={() => { setLat(''); setLon(''); setGeoLabel(''); }} className="ml-1 text-gray-500 hover:text-white underline" data-testid="tv-geocode-clear">clear</button>
          </p>
        ) : (
          <p className="text-[11px] text-gray-600 mt-2">No coordinates yet — weather stays hidden until a city is located.</p>
        )}
      </div>

      {/* Live preview */}
      <div className="mb-4">
        <p className="text-xs font-bold text-white uppercase tracking-wider mb-2">Live preview</p>
        <div className="aspect-video w-full rounded-2xl overflow-hidden ring-1 ring-white/10 bg-black">
          {previewSrc ? (
            <iframe key={previewSrc} src={previewSrc} title="TV preview" className="w-full h-full border-0" data-testid="tv-theme-preview-iframe" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">Preview unavailable</div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={saving} className="bg-[#FF007F] hover:bg-[#CC0066] text-white rounded-full px-5 h-10 text-[12px] font-bold tracking-wider" data-testid="tv-theme-save">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          SAVE THEME
        </Button>
        <Button onClick={openOnTV} variant="secondary" disabled={!venueCode} className="rounded-full px-5 h-10 text-[12px] font-bold tracking-wider gap-1.5" data-testid="tv-theme-preview-on-tv">
          <ExternalLink className="w-4 h-4" /> PREVIEW ON TV
        </Button>
      </div>
    </div>
  );
}

export default TVThemeCard;
